/**
 * Pure calculation functions for hero-KPIer på admin-dashboardet.
 * Erik-lock 2026-05-15 — se docs/kpi-source-of-truth.md.
 *
 * Disse er rene funksjoner uten Supabase- eller React-avhengigheter. Hooks
 * wrapper dem med fetch og caching.
 */

// VIB-1452 terminologi: `bf_*`-felter, funksjonsnavn og DB-kontrakter beholdes
// for bakoverkompatibilitet. Den samme metrikken heter alltid GEEK i UI,
// hjelp, rapporter og KPI-fasiten.

/**
 * Erik-regel for hvilke fakturastatuser som teller mot omsetning:
 * "så lenge fakturaen er fakturert teller den (inkl. overdue)". Kreditnotaer
 * er allerede lagret med negativt amount_excl_vat fra PO, så SUM netter
 * naturlig.
 *
 * Samme statusliste som brukes i sync-poweroffice-financials/index.ts:45-55.
 */
export const INVOICED_STATUSES = ["sent", "paid", "overdue", "credit_note"] as const;
export type InvoicedStatus = (typeof INVOICED_STATUSES)[number];

export function isInvoicedStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return (INVOICED_STATUSES as readonly string[]).includes(status);
}

/**
 * VIB-1238: hvilke bilag teller som kostnad i BF reell.
 *
 * `approved`        = bokført i PO (har voucher_no, fra AccountTransactions).
 * `forwarded_to_dl` = godkjent av Sølvrev (første linje), forwarded til DL og
 *                     venter på bokføring i PO. Committed kostnad, så den teller
 *                     allerede her — ellers oppstår et hull der et bilag som
 *                     bokføres etter prosjektlukking (frosset bf_actual) aldri
 *                     reflekteres. Bokføring oppdaterer SAMME rad
 *                     (`forwarded_to_dl`→`approved`), så ingen dobbelttelling.
 *
 * `rejected` og `pending_approval` teller ALDRI — sistnevnte er ikke godkjent
 * ennå, og avviser DL et forwarded-bilag i PO trekkes det automatisk tilbake
 * til `pending_approval` av `sync-poweroffice-voucher-approvals` (cron 6h).
 *
 * Holdes i sync med `recompute_project_bf` + `sync-poweroffice-financials` (SQL/edge).
 */
export const BF_COST_VOUCHER_STATUSES = ["approved", "forwarded_to_dl"] as const;

export function countsAsBfCost(status: string | null | undefined): boolean {
  if (!status) return false;
  return (BF_COST_VOUCHER_STATUSES as readonly string[]).includes(status);
}

export interface YtdComparison {
  /** Current year-to-date verdi i kroner */
  current: number;
  /** Forrige år, samme datointervall (Jan 1 → samme dag i fjor) */
  previousYear: number;
  /** Prosent endring. null hvis previousYear = 0 (ingen sammenligningsdata). */
  deltaPct: number | null;
}

interface InvoiceRow {
  invoice_date: string | null;
  status: string | null;
  amount_excl_vat: number | null;
  project_id?: string | null;
}

interface VoucherRow {
  voucher_date: string | null;
  amount: number | null;
  project_id?: string | null;
  status?: string | null;
}

/**
 * Sjekk om en ISO-dato faller i året `year` opp til (og med) `cutoff`-måned/dag.
 * Brukes til å plukke ut samme YTD-vindu i forrige år som vi er i nå.
 *
 * Eksempel: today = 2026-05-19 → kutter ved 2026-05-19. For previousYear-
 * sammenligning passerer vi `cutoff` = 2025-05-19. Datoer ≥ 2025-01-01 og
 * ≤ 2025-05-19 teller med.
 */
function isInYtdWindow(dateStr: string, year: number, cutoff: Date): boolean {
  // `dateStr` er en dato-only-streng (Postgres `date`-kolonne) — ECMAScript
  // tolker den alltid som UTC-midnatt uansett kjøretidssone. Resten av
  // funksjonen er konsekvent UTC (Date.UTC/getUTC*), så vi leser `date`
  // med UTC-gettere også — IKKE lokale gettere (VIB-1339: `getFullYear()`
  // uten UTC-prefiks ga feil år i tidssoner vest for UTC ved årsskiftet).
  const date = new Date(dateStr);
  if (date.getUTCFullYear() !== year) return false;
  // Bygg en cutoff i samme år som date
  const sameYearCutoff = new Date(
    Date.UTC(year, cutoff.getUTCMonth(), cutoff.getUTCDate()),
  );
  return date <= sameYearCutoff;
}

function calcDeltaPct(current: number, previousYear: number): number | null {
  if (previousYear === 0) return null;
  return Math.round(((current - previousYear) / previousYear) * 1000) / 10;
}

function comparisonCutoffs(
  today: Date,
  throughIso: string,
): { currentYear: number; previousYear: number; current: Date; previous: Date } | null {
  const currentYear = today.getUTCFullYear();
  const previousYear = currentYear - 1;
  const through = new Date(`${throughIso}T00:00:00Z`);
  if (Number.isNaN(through.getTime()) || through.getUTCFullYear() !== currentYear) {
    return null;
  }
  const month = through.getUTCMonth();
  const day = through.getUTCDate();
  return {
    currentYear,
    previousYear,
    current: new Date(Date.UTC(currentYear, month, day)),
    previous: new Date(Date.UTC(previousYear, month, day)),
  };
}

/**
 * Total omsetning (eks. MVA) hittil i år, med sammenligning mot samme periode
 * i fjor. Kreditnotaer trekker fra (negativ amount_excl_vat fra PO).
 *
 * Status-filter: sent, paid, overdue, credit_note (samme som PO-financials-sync).
 * Periode: invoice_date hittil i år vs samme datointervall i fjor.
 */
export function calcTotalRevenueYtd(
  invoices: InvoiceRow[],
  today: Date,
): YtdComparison {
  const currentYear = today.getUTCFullYear();
  const previousYear = currentYear - 1;

  let current = 0;
  let prev = 0;

  for (const inv of invoices) {
    if (!inv.invoice_date) continue;
    if (!isInvoicedStatus(inv.status)) continue;
    const amount = Number(inv.amount_excl_vat ?? 0);

    if (isInYtdWindow(inv.invoice_date, currentYear, today)) {
      current += amount;
    } else if (isInYtdWindow(inv.invoice_date, previousYear, today)) {
      prev += amount;
    }
  }

  return {
    current,
    previousYear: prev,
    deltaPct: calcDeltaPct(current, prev),
  };
}

/**
 * Omsetning per måned i `year`, samme statusfilter som calcTotalRevenueYtd.
 * Indeks 0 = januar. Brukes av drilldownen bak omsetningsflisen (VIB-2990).
 */
export function calcRevenueByMonth(invoices: InvoiceRow[], year: number): number[] {
  const months = Array<number>(12).fill(0);
  for (const inv of invoices) {
    if (!inv.invoice_date || !isInvoicedStatus(inv.status)) continue;
    if (Number(inv.invoice_date.slice(0, 4)) !== year) continue;
    months[Number(inv.invoice_date.slice(5, 7)) - 1] += Number(inv.amount_excl_vat ?? 0);
  }
  return months;
}

/**
 * Samme omsetningsformel som calcTotalRevenueYtd, men avgrenset til en
 * eksplisitt avstemt dato i inneværende år. Før årets første avstemte måned
 * returneres 0/null i stedet for å blande inn fjorårets måneder.
 */
export function calcTotalRevenueYtdThrough(
  invoices: InvoiceRow[],
  today: Date,
  throughIso: string,
): YtdComparison {
  const cutoffs = comparisonCutoffs(today, throughIso);
  if (!cutoffs) return { current: 0, previousYear: 0, deltaPct: null };

  let current = 0;
  let previousYear = 0;
  for (const inv of invoices) {
    if (!inv.invoice_date || !isInvoicedStatus(inv.status)) continue;
    const amount = Number(inv.amount_excl_vat ?? 0);
    if (isInYtdWindow(inv.invoice_date, cutoffs.currentYear, cutoffs.current)) {
      current += amount;
    } else if (
      isInYtdWindow(inv.invoice_date, cutoffs.previousYear, cutoffs.previous)
    ) {
      previousYear += amount;
    }
  }
  return { current, previousYear, deltaPct: calcDeltaPct(current, previousYear) };
}

// ── KPI #8 Total resultat (P&L fra PowerOffice, VIB-716) ───────────────

/**
 * Norsk kontoplan-konvensjon (NS 4102):
 *   Klasse 3xxx — Driftsinntekter
 *   Klasse 4xxx-7xxx — Driftskostnader
 *   Klasse 8xxx — Finansposter og skatt
 *
 * Disse rangene brukes for å skille inntekt fra kostnad i P&L-beregningen.
 */
const REVENUE_ACCOUNT_MIN = 3000;
const REVENUE_ACCOUNT_MAX = 3999;
const COST_ACCOUNT_MIN = 4000;
const COST_ACCOUNT_MAX = 8999;
const COST_OF_GOODS_ACCOUNT_MAX = 4999;

export interface PoTransaction {
  accountNo: number;
  /** PO returnerer kredit som negativ, debet som positiv */
  amount: number;
  /** ISO-dato, YYYY-MM-DD */
  postingDate: string;
}

/**
 * Regnskapsmessig bruttofortjeneste fra PowerOffice.
 *
 * Norsk standard kontoplan:
 * - driftsinntekter: 3xxx (kredit kommer som negativt beløp fra PO)
 * - varekostnad/direkte kostnad: 4xxx
 *
 * Dette er bevisst en annen metrikk enn operativ GEEK, som beregnes fra
 * prosjektfakturaer minus prosjektkoblede faktiske kostnader.
 */
export function calcTotalGrossProfitYtd(
  transactions: PoTransaction[],
  today: Date,
): YtdComparison {
  const currentYear = today.getUTCFullYear();
  const previousYear = currentYear - 1;
  let currentRevenue = 0;
  let currentCostOfGoods = 0;
  let previousRevenue = 0;
  let previousCostOfGoods = 0;

  for (const tx of transactions) {
    if (!tx.postingDate) continue;
    const isRevenue = tx.accountNo >= REVENUE_ACCOUNT_MIN && tx.accountNo <= REVENUE_ACCOUNT_MAX;
    const isCostOfGoods = tx.accountNo >= COST_ACCOUNT_MIN && tx.accountNo <= COST_OF_GOODS_ACCOUNT_MAX;
    if (!isRevenue && !isCostOfGoods) continue;

    if (isInYtdWindow(tx.postingDate, currentYear, today)) {
      if (isRevenue) currentRevenue += -tx.amount;
      else currentCostOfGoods += tx.amount;
    } else if (isInYtdWindow(tx.postingDate, previousYear, today)) {
      if (isRevenue) previousRevenue += -tx.amount;
      else previousCostOfGoods += tx.amount;
    }
  }

  const current = currentRevenue - currentCostOfGoods;
  const previous = previousRevenue - previousCostOfGoods;
  return {
    current,
    previousYear: previous,
    deltaPct: calcDeltaPct(current, previous),
  };
}

/** Regnskapsmessig bruttofortjeneste gjennom siste avstemte posteringsdato. */
export function calcTotalGrossProfitYtdThrough(
  transactions: PoTransaction[],
  today: Date,
  throughIso: string,
): YtdComparison {
  const cutoffs = comparisonCutoffs(today, throughIso);
  if (!cutoffs) return { current: 0, previousYear: 0, deltaPct: null };

  let currentRevenue = 0;
  let currentCostOfGoods = 0;
  let previousRevenue = 0;
  let previousCostOfGoods = 0;

  for (const tx of transactions) {
    if (!tx.postingDate) continue;
    const isRevenue = tx.accountNo >= REVENUE_ACCOUNT_MIN && tx.accountNo <= REVENUE_ACCOUNT_MAX;
    const isCostOfGoods = tx.accountNo >= COST_ACCOUNT_MIN && tx.accountNo <= COST_OF_GOODS_ACCOUNT_MAX;
    if (!isRevenue && !isCostOfGoods) continue;

    if (isInYtdWindow(tx.postingDate, cutoffs.currentYear, cutoffs.current)) {
      if (isRevenue) currentRevenue += -tx.amount;
      else currentCostOfGoods += tx.amount;
    } else if (isInYtdWindow(tx.postingDate, cutoffs.previousYear, cutoffs.previous)) {
      if (isRevenue) previousRevenue += -tx.amount;
      else previousCostOfGoods += tx.amount;
    }
  }

  const current = currentRevenue - currentCostOfGoods;
  const previousYear = previousRevenue - previousCostOfGoods;
  return { current, previousYear, deltaPct: calcDeltaPct(current, previousYear) };
}

/** Kontoklassene i resultatet, 3 er inntekt og 4–8 kostnad. */
export const RESULT_ACCOUNT_CLASSES = [3, 4, 5, 6, 7, 8] as const;
export type ResultAccountClass = (typeof RESULT_ACCOUNT_CLASSES)[number];

export interface ResultMonth {
  revenue: number;
  cost: number;
  result: number;
  /** Beløp per kontoklasse. Inntekt (3) er positiv, kostnader (4–8) positive som kostnad. */
  byClass: Record<ResultAccountClass, number>;
}

/**
 * Resultatet per måned i `year`, samme kontoregel som calcTotalResultYtd:
 * inntekt = −Amount på 3000–3999, kostnad = Amount på 4000–8999.
 * Indeks 0 = januar (VIB-2990).
 */
export function calcResultByMonth(transactions: PoTransaction[], year: number): ResultMonth[] {
  const months: ResultMonth[] = Array.from({ length: 12 }, () => ({
    revenue: 0,
    cost: 0,
    result: 0,
    byClass: { 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0 },
  }));
  for (const tx of transactions) {
    if (!tx.postingDate || Number(tx.postingDate.slice(0, 4)) !== year) continue;
    const isRevenue = tx.accountNo >= REVENUE_ACCOUNT_MIN && tx.accountNo <= REVENUE_ACCOUNT_MAX;
    const isCost = tx.accountNo >= COST_ACCOUNT_MIN && tx.accountNo <= COST_ACCOUNT_MAX;
    if (!isRevenue && !isCost) continue;
    const m = months[Number(tx.postingDate.slice(5, 7)) - 1];
    const cls = Math.floor(tx.accountNo / 1000) as ResultAccountClass;
    if (isRevenue) {
      m.revenue += -tx.amount;
      m.byClass[3] += -tx.amount;
    } else {
      m.cost += tx.amount;
      m.byClass[cls] += tx.amount;
    }
  }
  for (const m of months) m.result = m.revenue - m.cost;
  return months;
}

/**
 * Beregn total resultat (P&L) YTD med sammenligning mot forrige år.
 *
 * Per Erik-lock 2026-05-15 (SOT § Totalt resultat):
 *   revenue = Σ -Amount for AccountNo i 3000-3999 (negér fordi PO returnerer kredit som negativ)
 *   cost    = Σ Amount for AccountNo i 4000-8999
 *   result  = revenue - cost
 *
 * Forrige år sammenlignes på samme datointervall (Jan 1 → samme dag-i-måned).
 */
export function calcTotalResultYtd(
  transactions: PoTransaction[],
  today: Date,
): YtdComparison {
  const currentYear = today.getUTCFullYear();
  const previousYear = currentYear - 1;

  let currentRevenue = 0;
  let currentCost = 0;
  let prevRevenue = 0;
  let prevCost = 0;

  for (const tx of transactions) {
    if (!tx.postingDate) continue;
    const isRevenue = tx.accountNo >= REVENUE_ACCOUNT_MIN && tx.accountNo <= REVENUE_ACCOUNT_MAX;
    const isCost = tx.accountNo >= COST_ACCOUNT_MIN && tx.accountNo <= COST_ACCOUNT_MAX;
    if (!isRevenue && !isCost) continue;

    if (isInYtdWindow(tx.postingDate, currentYear, today)) {
      if (isRevenue) currentRevenue += -tx.amount;
      else currentCost += tx.amount;
    } else if (isInYtdWindow(tx.postingDate, previousYear, today)) {
      if (isRevenue) prevRevenue += -tx.amount;
      else prevCost += tx.amount;
    }
  }

  const current = currentRevenue - currentCost;
  const prev = prevRevenue - prevCost;

  return {
    current,
    previousYear: prev,
    deltaPct: calcDeltaPct(current, prev),
  };
}

/** Resultat hittil i år gjennom en eksplisitt avstemt posteringsdato. */
export function calcTotalResultYtdThrough(
  transactions: PoTransaction[],
  today: Date,
  throughIso: string,
): YtdComparison {
  const cutoffs = comparisonCutoffs(today, throughIso);
  if (!cutoffs) return { current: 0, previousYear: 0, deltaPct: null };

  let currentRevenue = 0;
  let currentCost = 0;
  let previousRevenue = 0;
  let previousCost = 0;

  for (const tx of transactions) {
    if (!tx.postingDate) continue;
    const isRevenue = tx.accountNo >= REVENUE_ACCOUNT_MIN && tx.accountNo <= REVENUE_ACCOUNT_MAX;
    const isCost = tx.accountNo >= COST_ACCOUNT_MIN && tx.accountNo <= COST_ACCOUNT_MAX;
    if (!isRevenue && !isCost) continue;

    if (isInYtdWindow(tx.postingDate, cutoffs.currentYear, cutoffs.current)) {
      if (isRevenue) currentRevenue += -tx.amount;
      else currentCost += tx.amount;
    } else if (
      isInYtdWindow(tx.postingDate, cutoffs.previousYear, cutoffs.previous)
    ) {
      if (isRevenue) previousRevenue += -tx.amount;
      else previousCost += tx.amount;
    }
  }

  const current = currentRevenue - currentCost;
  const previousYear = previousRevenue - previousCost;
  return { current, previousYear, deltaPct: calcDeltaPct(current, previousYear) };
}

// ── KPI #3 Verdi per ansatt + KPI #4 Snitt timespris (VIB-713) ──────────

/**
 * Per Erik-lock 2026-05-15: assignments med disse statusene teller mot verdi-
 * bidrag. Samme regel som brukes for Faktureringsgrad.
 */
const COUNTED_TASK_STATUSES = new Set(["completed", "approved"]);

export interface ValueProjectRow {
  project_id: string;
  project_name: string;
  /** Ansattens timer på prosjektet i perioden */
  employeeHours: number;
  /** Alle ansattes timer på prosjektets approved/completed assignments (lifetime) */
  totalProjectHours: number;
  /** Ansattens andel av total-timer på prosjektet */
  share: number;
  /** Prosjektets bf_actual (lifetime fra projects-tabellen) */
  projectBfActual: number;
  /** Ansattens kr-andel: projectBfActual × share */
  value: number;
}

export interface EmployeeValueContribution {
  /** YTD totalsum i kroner */
  ytdValue: number;
  /** Per-prosjekt breakdown, sortert descending på value */
  byProject: ValueProjectRow[];
}

interface AssignmentLike {
  user_id: string;
  task_status: string;
  completed_at: string | null;
  budget_line_id: string;
  budget_lines: { project_id: string } | null;
}

interface TimeEntryLike {
  user_id: string | null;
  duration_seconds: number | null;
  budget_line_id: string | null;
}

interface ProjectLike {
  id: string;
  name: string;
  bf_actual: number | null;
}

/**
 * Beregn ansattens verdi-bidrag (KPI #3) per prosjekt for et periode-vindu.
 *
 * Per Erik-lock 2026-05-15 i SOT § Verdi per ansatt:
 *   For hver ASSIGNMENT der user_id=E, status IN ('completed','approved'),
 *   completed_at i periode P:
 *     Pa = budget_line.project_id
 *     bf_Pa = projects.bf_actual (lifetime)
 *     e_hours_on_Pa_in_P = Σ time_entries for ansatten på den budget_line
 *     total_hours_on_Pa = Σ time_entries på ALLE approved/completed
 *                        assignments på prosjektet (lifetime, alle brukere)
 *     hvis total_hours_on_Pa > 0:
 *       share = e_hours_on_Pa_in_P / total_hours_on_Pa
 *       value_Pa += bf_Pa × share
 *
 * Aggregeres på prosjekt-nivå (ikke per-assignment) for å unngå dobbel-
 * telling hvis ansatten har flere assignments på samme prosjekt.
 */
export function calcEmployeeValueContribution(input: {
  userId: string;
  /** Alle assignments (alle brukere, alle statuser — vi filtrerer her) */
  assignments: AssignmentLike[];
  /** Alle time_entries (alle brukere, lifetime) */
  timeEntries: TimeEntryLike[];
  projects: ProjectLike[];
  /** Periode-start (typisk Jan 1 inneværende år) */
  periodStart: Date;
  /** Periode-slutt eksklusiv (typisk i dag) */
  periodEnd: Date;
}): EmployeeValueContribution {
  const { userId, assignments, timeEntries, projects, periodStart, periodEnd } = input;

  // Steg 1: finn ansattens assignments i perioden, gruppert per prosjekt
  const employeeBudgetLineIdsByProject = new Map<string, Set<string>>();
  for (const a of assignments) {
    if (a.user_id !== userId) continue;
    if (!COUNTED_TASK_STATUSES.has(a.task_status)) continue;
    if (!a.completed_at) continue;
    if (!a.budget_lines) continue;

    const date = new Date(a.completed_at);
    if (date < periodStart || date >= periodEnd) continue;

    const projectId = a.budget_lines.project_id;
    if (!projectId) continue;

    const set = employeeBudgetLineIdsByProject.get(projectId) ?? new Set<string>();
    set.add(a.budget_line_id);
    employeeBudgetLineIdsByProject.set(projectId, set);
  }

  // Steg 2: bygg map av total-timer-per-budget_line (lifetime, alle brukere)
  // Brukes for nevner i share-beregning. Vi trenger kun budget_lines som er
  // del av approved/completed assignments — bygg en lookup først.
  const countedBudgetLinesByProject = new Map<string, Set<string>>();
  for (const a of assignments) {
    if (!COUNTED_TASK_STATUSES.has(a.task_status)) continue;
    if (!a.budget_lines) continue;
    const projectId = a.budget_lines.project_id;
    if (!projectId) continue;
    const set = countedBudgetLinesByProject.get(projectId) ?? new Set<string>();
    set.add(a.budget_line_id);
    countedBudgetLinesByProject.set(projectId, set);
  }

  const totalHoursPerBudgetLine = new Map<string, number>();
  const employeeHoursPerBudgetLine = new Map<string, number>();
  for (const te of timeEntries) {
    if (!te.budget_line_id) continue;
    const hours = (te.duration_seconds ?? 0) / 3600;
    totalHoursPerBudgetLine.set(
      te.budget_line_id,
      (totalHoursPerBudgetLine.get(te.budget_line_id) ?? 0) + hours,
    );
    if (te.user_id === userId) {
      employeeHoursPerBudgetLine.set(
        te.budget_line_id,
        (employeeHoursPerBudgetLine.get(te.budget_line_id) ?? 0) + hours,
      );
    }
  }

  // Steg 3: per prosjekt — beregn share og value
  const projectMap = new Map(projects.map((p) => [p.id, p]));
  const rows: ValueProjectRow[] = [];

  for (const [projectId, employeeBudgetLineIds] of employeeBudgetLineIdsByProject) {
    const project = projectMap.get(projectId);
    if (!project) continue;
    const projectBfActual = Number(project.bf_actual ?? 0);

    // Ansattens timer på sine budget_lines på prosjektet (lifetime — share-modellen
    // bruker total timer på prosjektet som nevner uansett periode)
    let employeeHours = 0;
    for (const blId of employeeBudgetLineIds) {
      employeeHours += employeeHoursPerBudgetLine.get(blId) ?? 0;
    }

    // Totale timer på ALLE approved/completed budget_lines på prosjektet (lifetime, alle brukere)
    const allCountedBls = countedBudgetLinesByProject.get(projectId) ?? new Set<string>();
    let totalProjectHours = 0;
    for (const blId of allCountedBls) {
      totalProjectHours += totalHoursPerBudgetLine.get(blId) ?? 0;
    }

    if (totalProjectHours <= 0) continue; // unngå null-divisjon

    const share = employeeHours / totalProjectHours;
    const value = projectBfActual * share;

    rows.push({
      project_id: projectId,
      project_name: project.name,
      employeeHours: Math.round(employeeHours * 10) / 10,
      totalProjectHours: Math.round(totalProjectHours * 10) / 10,
      share: Math.round(share * 10000) / 10000, // 4 desimaler for presisjon
      projectBfActual,
      value: Math.round(value),
    });
  }

  rows.sort((a, b) => b.value - a.value);
  const ytdValue = rows.reduce((s, r) => s + r.value, 0);

  return { ytdValue, byProject: rows };
}

/**
 * Snitt timespris (KPI #4) for en ansatt: verdi / alle loggede timer i perioden.
 *
 * Erik-lock 2026-05-15: nevneren er ALLE loggede timer (uten task-status-filter)
 * — også interne prosjekter og ikke-fakturerbar tid. Insentivet er at all
 * arbeidstid skal generere mest mulig BF.
 *
 * Returnerer null hvis det ikke er logget timer i perioden.
 */
export function calcEmployeeAvgHourlyRate(
  value: number,
  totalHoursInPeriod: number,
): number | null {
  if (totalHoursInPeriod <= 0) return null;
  return Math.round(value / totalHoursInPeriod);
}

/**
 * Org-snitt-timepris for samme periode (brukes som sammenligningsgrunnlag for
 * KPI #4 hero-kort). Returnerer 0 hvis det ikke er logget timer org-wide.
 */
export function calcOrgAvgHourlyRate(
  orgTotalValue: number,
  orgTotalHours: number,
): number {
  if (orgTotalHours <= 0) return 0;
  return Math.round(orgTotalValue / orgTotalHours);
}

/**
 * Total bruttofortjeneste hittil i år. Beregnes per prosjekt og summeres:
 *   bf_ytd = Σ invoices.amount_excl_vat (YTD, invoiced status)
 *          − Σ vouchers.amount (YTD)
 * Prosjekter uten omsetning i året ekskluderes. Negative BF inkluderes —
 * ærlig regnskap, tap drar ned org-total.
 *
 * Erik-lock 2026-05-15: matcher per-prosjekt-formelen i
 * sync-poweroffice-financials, men live-beregnet og YTD-filtrert.
 */
export function calcTotalBfYtd(
  invoices: InvoiceRow[],
  vouchers: VoucherRow[],
  today: Date,
): YtdComparison {
  const currentYear = today.getUTCFullYear();
  const previousYear = currentYear - 1;

  // Per-prosjekt-aggregat for hvert år
  const revenueCurrent = new Map<string, number>();
  const costCurrent = new Map<string, number>();
  const revenuePrev = new Map<string, number>();
  const costPrev = new Map<string, number>();

  for (const inv of invoices) {
    if (!inv.invoice_date || !inv.project_id) continue;
    if (!isInvoicedStatus(inv.status)) continue;
    const amount = Number(inv.amount_excl_vat ?? 0);

    if (isInYtdWindow(inv.invoice_date, currentYear, today)) {
      revenueCurrent.set(inv.project_id, (revenueCurrent.get(inv.project_id) ?? 0) + amount);
    } else if (isInYtdWindow(inv.invoice_date, previousYear, today)) {
      revenuePrev.set(inv.project_id, (revenuePrev.get(inv.project_id) ?? 0) + amount);
    }
  }

  for (const v of vouchers) {
    if (!v.voucher_date || !v.project_id) continue;
    // VIB-1238: kun godkjente/bokførte bilag teller (ekskluderer rejected/pending).
    if (!countsAsBfCost(v.status)) continue;
    const amount = Number(v.amount ?? 0);

    if (isInYtdWindow(v.voucher_date, currentYear, today)) {
      costCurrent.set(v.project_id, (costCurrent.get(v.project_id) ?? 0) + amount);
    } else if (isInYtdWindow(v.voucher_date, previousYear, today)) {
      costPrev.set(v.project_id, (costPrev.get(v.project_id) ?? 0) + amount);
    }
  }

  // Erik-lock: kun prosjekter med omsetning i året teller — ekskluderer
  // aktive prosjekter uten inntekt så vi ikke skjuler tap fra fjorår.
  let current = 0;
  for (const [projectId, revenue] of revenueCurrent) {
    current += revenue - (costCurrent.get(projectId) ?? 0);
  }
  let prev = 0;
  for (const [projectId, revenue] of revenuePrev) {
    prev += revenue - (costPrev.get(projectId) ?? 0);
  }

  return {
    current,
    previousYear: prev,
    deltaPct: calcDeltaPct(current, prev),
  };
}

/**
 * Reell GEEK gjennom en eksplisitt avstemt dato:
 * alle prosjektkoblede fakturaer minus alle faktiske prosjektkostnader.
 *
 * I motsetning til `calcTotalBfYtd` trekkes også kostnader på prosjekter som
 * ennå ikke har fakturert i perioden. Bonusgrunnlaget skal ikke bli kunstig
 * høyt bare fordi fakturaen kommer etter kostnaden.
 */
export function calcTotalGeekYtdThrough(
  invoices: InvoiceRow[],
  vouchers: VoucherRow[],
  today: Date,
  throughIso: string,
): YtdComparison {
  const cutoffs = comparisonCutoffs(today, throughIso);
  if (!cutoffs) return { current: 0, previousYear: 0, deltaPct: null };

  let revenueCurrent = 0;
  let costCurrent = 0;
  let revenuePrevious = 0;
  let costPrevious = 0;

  for (const invoice of invoices) {
    if (!invoice.invoice_date || !invoice.project_id) continue;
    if (!isInvoicedStatus(invoice.status)) continue;
    const amount = Number(invoice.amount_excl_vat ?? 0);

    if (isInYtdWindow(invoice.invoice_date, cutoffs.currentYear, cutoffs.current)) {
      revenueCurrent += amount;
    } else if (
      isInYtdWindow(invoice.invoice_date, cutoffs.previousYear, cutoffs.previous)
    ) {
      revenuePrevious += amount;
    }
  }

  for (const voucher of vouchers) {
    if (!voucher.voucher_date || !voucher.project_id) continue;
    if (!countsAsBfCost(voucher.status)) continue;
    const amount = Number(voucher.amount ?? 0);

    if (isInYtdWindow(voucher.voucher_date, cutoffs.currentYear, cutoffs.current)) {
      costCurrent += amount;
    } else if (
      isInYtdWindow(voucher.voucher_date, cutoffs.previousYear, cutoffs.previous)
    ) {
      costPrevious += amount;
    }
  }

  const current = revenueCurrent - costCurrent;
  const previousYear = revenuePrevious - costPrevious;

  return {
    current,
    previousYear,
    deltaPct: calcDeltaPct(current, previousYear),
  };
}
